import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface ClickEvent {
  id: string;
  element: string;
  text: string;
  page: string;
  timestamp: string;
  path: string;
  x: number;
  y: number;
}

class TrackingService {
  private events: ClickEvent[] = [];
  private sessionId: string;

  constructor() {
    this.sessionId = this.generateSessionId();
    console.log('%c[TRACKING] Session started:', 'color: #3B6CFF; font-weight: bold;', this.sessionId);
  }

  private generateSessionId(): string {
    return 'sess_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
  }

  trackClick(event: React.MouseEvent, element: HTMLElement) {
    const clickEvent: ClickEvent = {
      id: `click_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      element: element.tagName.toLowerCase(),
      text: element.textContent?.trim().substring(0, 50) || 'no-text',
      page: document.title,
      timestamp: new Date().toISOString(),
      path: window.location.pathname,
      x: event.clientX,
      y: event.clientY,
    };

    this.events.push(clickEvent);
    
    // Log to console with styling
    console.log(
      '%c[CLICK]',
      'background: #3B6CFF; color: white; padding: 2px 6px; border-radius: 4px; font-weight: bold;',
      {
        element: clickEvent.element,
        text: clickEvent.text,
        page: clickEvent.path,
        time: new Date(clickEvent.timestamp).toLocaleTimeString(),
        position: `(${clickEvent.x}, ${clickEvent.y})`,
      }
    );

    // Here you could also send to an analytics endpoint
    // this.sendToAnalytics(clickEvent);
  }

  getEvents(): ClickEvent[] {
    return [...this.events];
  }

  getStats() {
    const stats = {
      totalClicks: this.events.length,
      clicksByPage: {} as Record<string, number>,
      clicksByElement: {} as Record<string, number>,
      mostClicked: [] as { text: string; count: number }[],
    };

    this.events.forEach(event => {
      // Count by page
      stats.clicksByPage[event.path] = (stats.clicksByPage[event.path] || 0) + 1;
      
      // Count by element type
      stats.clicksByElement[event.element] = (stats.clicksByElement[event.element] || 0) + 1;
    });

    // Find most clicked elements
    const textCounts: Record<string, number> = {};
    this.events.forEach(event => {
      textCounts[event.text] = (textCounts[event.text] || 0) + 1;
    });
    
    stats.mostClicked = Object.entries(textCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([text, count]) => ({ text, count }));

    return stats;
  }

  printStats() {
    const stats = this.getStats();
    console.log('%c[TRACKING STATS]', 'background: #0B1220; color: #7AA8FF; padding: 4px 8px; border-radius: 4px; font-weight: bold;');
    console.table(stats);
  }

  clear() {
    this.events = [];
    console.log('%c[TRACKING] Events cleared', 'color: #3B6CFF;');
  }
}

// Singleton instance
export const trackingService = new TrackingService();

// Hook for tracking clicks
export function useTracking() {
  const location = useLocation();
  const trackedElements = useRef<Set<HTMLElement>>(new Set());

  const trackElement = useCallback((element: HTMLElement | null) => {
    if (!element || trackedElements.current.has(element)) return;

    trackedElements.current.add(element);
    
    const handleClick = (e: Event) => {
      trackingService.trackClick(e as unknown as React.MouseEvent, element);
    };

    element.addEventListener('click', handleClick);
    
    return () => {
      element.removeEventListener('click', handleClick);
      trackedElements.current.delete(element);
    };
  }, []);

  // Track page views
  useEffect(() => {
    console.log(
      '%c[PAGE VIEW]',
      'background: #10B981; color: white; padding: 2px 6px; border-radius: 4px; font-weight: bold;',
      {
        path: location.pathname,
        title: document.title,
        time: new Date().toLocaleTimeString(),
      }
    );
  }, [location.pathname]);

  return { trackElement, trackingService };
}

// Make tracking available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).tracking = trackingService;
}
